/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import FormValidations from 'cio/utils/form-validations';
import { action, computed } from '@ember/object';
import { task } from 'ember-concurrency';
import SubscriptionTopicModel from 'cio/models/subscription-topic';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import SubscriptionCenterService from 'cio/services/subscription-center';
import SubscriptionLanguageModel from 'cio/models/subscription-language';
import DS from 'ember-data';
import { MAX_NAME_LENGTH, MAX_DESCRIPTION_LENGTH } from 'cio/utils/subscription-center';

interface EditModalArgs {
  existingTopics?: SubscriptionTopicModel[];
  topic: SubscriptionTopicModel;
  readOnly?: boolean;
  action: 'create' | 'edit';
  onAction: (topic: SubscriptionTopicModel) => unknown;
  onClose: () => unknown;
  subscribedPeopleTotal: number;
  languages?: SubscriptionLanguageModel[];
}

const Validations = {
  name(this: EditModal, value?: string) {
    if (isEmpty(value?.trim())) {
      return 'Please enter a name';
    }

    let topicAlreadyExists: boolean;
    if (this.args.action === 'edit') {
      topicAlreadyExists =
        this.topics?.some(
          (topic) => this.currentTopicName !== topic.name && topic.name.toLowerCase() === value?.toLowerCase(),
        ) ?? false;
    } else {
      topicAlreadyExists = this.topics?.some((topic) => topic.name.toLowerCase() === value?.toLowerCase()) ?? false;
    }

    if (this.submitTask.isRunning && topicAlreadyExists) {
      return `The ${value} topic already exists`;
    }

    if ((value?.length ?? 0) > MAX_NAME_LENGTH) {
      return `Name can't be longer than ${MAX_NAME_LENGTH} characters`;
    }

    return true;
  },

  description(value?: string) {
    if ((value?.length ?? 0) > MAX_DESCRIPTION_LENGTH) {
      return `Description can't be longer than ${MAX_DESCRIPTION_LENGTH} characters`;
    }

    return true;
  },
};

export default class EditModal extends Component<EditModalArgs> {
  @service subscriptionCenter!: SubscriptionCenterService;
  @service store!: DS.Store;

  readonly MAX_NAME_LENGTH = MAX_NAME_LENGTH;
  readonly MAX_DESCRIPTION_LENGTH = MAX_DESCRIPTION_LENGTH;

  @tracked confirmSaveTopic = false;
  @tracked showBanner = true;
  @tracked topicFields = {
    name: this.args.topic.name,
    description: this.args.topic.description,
  };

  currentTopicName = this.args.topic.name;

  validations: FormValidations;

  constructor(owner: unknown, args: EditModalArgs) {
    super(owner, args);

    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'topicFields',
    });
  }

  @computed('args.existingTopics', 'subscriptionCenter.topics.[]')
  get topics() {
    return this.args.existingTopics ?? this.subscriptionCenter.topics;
  }

  get enabledLanguages() {
    return this.args.languages?.filter((lang) => lang.enabled) ?? [];
  }

  @computed('args.readOnly', 'validations.hasErrors')
  get submitDisabled() {
    let { readOnly } = this.args;
    let { hasErrors } = this.validations;

    return Boolean(readOnly) || hasErrors || this.submitTask.isRunning;
  }

  @action
  validateAttribute(attr: string) {
    this.validations.validateAttribute(attr);
  }

  @action
  updateName({ target }: FlyKeyboardEvent<HTMLInputElement>) {
    let name = target.value;
    this.topicFields = { ...this.topicFields, name };
    this.validations.validateAttribute('name');
  }

  @action
  updateDescription({ target }: FlyKeyboardEvent<HTMLTextAreaElement>) {
    let description = target.value;
    this.topicFields = { ...this.topicFields, description };
    this.validations.validateAttribute('description');
  }

  @action
  toggleOptIn() {
    this.args.topic.set('optIn', !this.args.topic.optIn);
  }

  @action
  async openConfirmSaveModal() {
    try {
      await this.validations.validateModel();
      this.confirmSaveTopic = true;
    } catch {
      // `validateModel` already set validation errors, return
      return;
    }
  }

  submitTask = task(async () => {
    try {
      await this.validations.validateModel();
      let { onAction, topic } = this.args;
      let { name, description } = this.topicFields;
      topic.setProperties({
        name: name?.trim() ?? '',
        description: description?.trim() ?? '',
      });
      await onAction(topic);
    } catch {
      // `validateModel` already set validation errors, return early without submitting
      // `onAction`
      return;
    }
  });
}
